import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { EventDialogComponent } from "../timelog-dialog/event-dialog.component";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  FormComponent,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import {
  IMPORT_EVENT_CONTROLS,
  IMPORT_EVENT_SECTIONS,
} from "./import-event-form";
import { EventsService } from "../../services/inspection-events-service/events.service";
import {
  ApiErrorType,
  ApiResponseError,
} from "src/app/session/services/error-handler/api-response-error";
export enum DialogMode {
  import,
  warning,
  confirm,
}

@Component({
  selector: "im-import-event-dialog",
  templateUrl: "./import-event-dialog.component.html",
  styleUrl: "./import-event-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImportEventDialogComponent implements OnInit {
  @ViewChild("naturalForm")
  naturalForm: FormComponent;

  private inspectionId: string = "";

  private selectedInspection: any;
  private selectedItem: any;
  private currentInspectionSelectedItem: any;

  public icon: string = "cloud_upload";
  public title: string = "Import primary Item events from inspection";
  public leftButtonText: string = "CANCEL";
  public rightButtonText: string = "IMPORT";
  public textAboveDVE = {
    firstLine: "",
    secondLine: "",
    firstLabel: "",
    firstItem: "",
    secondLabel: "",
    secondItem: "",
    infoLine: "",
  };

  public isLoading: boolean = true;
  public showErrorMessage: boolean = false;
  public dialogMode = DialogMode.import;

  public sections: Section[] = [];
  public controls: any;
  public formConfig: NaturalFormConfig = <NaturalFormConfig>{
    displayMode: NaturalFormStyle.flat,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
  };

  constructor(
    private eventService: EventsService,
    private dialogRef: MatDialogRef<EventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
  ) {}

  async ngOnInit(): Promise<void> {
    this.inspectionId = this.dialogData;
    await this.fillImportItemsForm();
  }

  private async fillImportItemsForm(): Promise<void> {
    this.sections = JSON.parse(JSON.stringify(IMPORT_EVENT_SECTIONS));
    this.controls = JSON.parse(JSON.stringify(IMPORT_EVENT_CONTROLS));
    const inspections = await this.eventService.getImportEventsInspections(
      this.inspectionId,
    );
    let inspectionOptions = this.controls.find(
      (c) => c.id == "inspectionId",
    )?.options;
    inspections.forEach((i) => {
      inspectionOptions.push({ id: i.key, name: i.value });
    });
    this.isLoading = false;
  }

  private fitDialogData(mode: DialogMode): void {
    if (mode == DialogMode.import) {
      this.selectedInspection.visible = true;
      this.selectedItem.visible = true;
      this.currentInspectionSelectedItem.visible = false;
      this.currentInspectionSelectedItem.valid = true;
      this.icon = "cloud_upload";
      this.title = "Import primary Item events from inspection";
      this.leftButtonText = "CANCEL";
      this.rightButtonText = "IMPORT";
      this.textAboveDVE = {
        firstLine: "",
        secondLine: "",
        firstLabel: "",
        firstItem: "",
        secondLabel: "",
        secondItem: "",
        infoLine: "",
      };
    } else if (mode == DialogMode.warning) {
      this.selectedInspection.visible = false;
      this.selectedItem.visible = false;
      this.currentInspectionSelectedItem.visible = true;
      if (!this.currentInspectionSelectedItem.value)
        this.currentInspectionSelectedItem.valid = false;
      this.icon = "error_outline";
      this.title = "Item name doesn't exist on this inspection";
      this.textAboveDVE = {
        firstLine: `Warning! The selected Primary Item Name is not available on the current inspection`,
        secondLine: `You can select another exiting Item from the current Inspection and import the related Events`,
        firstLabel: "Original Inspection ID",
        firstItem: this.selectedInspection.readableValue,
        secondLabel: "Origin Item ID",
        secondItem: this.selectedItem.readableValue,
        infoLine: `Select an existing item to import Events instead`,
      };
      this.leftButtonText = "BACK";
      this.rightButtonText = "CONTINUE";
    } else {
      this.icon = "error_outline";
      this.title = "Confirm Item Events Import";
      this.currentInspectionSelectedItem.visible = false;
      this.textAboveDVE = {
        firstLine: `You are about to import events with different origen Item name`,
        secondLine: "",
        firstLabel: "Original Item Events",
        firstItem: this.selectedItem.readableValue,
        secondLabel: "New Item Events",
        secondItem: this.currentInspectionSelectedItem.readableValue,
        infoLine: `Do you want to proceed?`,
      };
      this.leftButtonText = "BACK";
      this.rightButtonText = "IMPORT";
    }
  }

  public async onFieldChange(fieldChange: any): Promise<void> {
    if (fieldChange.id == "inspectionId") {
      this.isLoading = true;
      this.controls.find((c) => c.id == "primaryItem").options = [];
      let primaryItemSection = this.sections[0].fields.find(
        (s) => s.id == "primaryItem",
      );
      primaryItemSection.value = "";
      primaryItemSection.readableValue = "";
      primaryItemSection.valid = false;

      const primaryItems =
        await this.eventService.getImportEventsItemsByInspection(
          fieldChange.value,
          true,
        );
      let primaryItemsOptions = this.controls.find(
        (c) => c.id == "primaryItem",
      )?.options;
      primaryItems.forEach((pi) => {
        primaryItemsOptions.push({ id: pi.key, name: pi.value });
      });
      this.isLoading = false;
    }
  }

  public onLeftButtonClick(): void {
    if (this.dialogMode == DialogMode.import) {
      this.dialogRef.close(null);
    } else if (this.dialogMode == DialogMode.warning) {
      this.dialogMode = DialogMode.import;
      this.fitDialogData(this.dialogMode);
    } else {
      this.dialogMode = DialogMode.warning;
      this.fitDialogData(this.dialogMode);
    }
  }

  public async onRightButtonClick(): Promise<void> {
    this.selectedInspection = this.sections[0].fields.find(
      (f) => f.id == "inspectionId",
    );
    this.selectedItem = this.sections[0].fields.find(
      (f) => f.id == "primaryItem",
    );
    this.currentInspectionSelectedItem = this.sections[0].fields.find(
      (f) => f.id == "currentInspecionItem",
    );
    if (this.dialogMode == DialogMode.import) {
      try {
        await this.eventService.importEventsFromInspection(
          this.selectedInspection.value,
          this.selectedItem.value,
          this.inspectionId,
        );
        this.dialogRef.close(true);
      } catch (e) {
        if (
          e instanceof ApiResponseError &&
          e.type == ApiErrorType.NO_CONTENT
        ) {
          this.showErrorMessage = false;
          this.controls.find((c) => c.id == "currentInspecionItem").options =
            [];
          const currentInspectionItems =
            await this.eventService.getImportEventsItemsByInspection(
              this.inspectionId,
              false,
            );
          let currentInspectionOptions = this.controls.find(
            (c) => c.id == "currentInspecionItem",
          )?.options;
          currentInspectionItems.forEach((cii) => {
            currentInspectionOptions.push({ id: cii.key, name: cii.value });
          });
          this.dialogMode = DialogMode.warning;
          this.fitDialogData(this.dialogMode);
        } else if (
          e instanceof ApiResponseError &&
          e.type == ApiErrorType.CONFLICT
        ) {
          this.showErrorMessage = true;
        }
      }
    } else if (this.dialogMode == DialogMode.warning) {
      this.dialogMode = DialogMode.confirm;
      this.fitDialogData(this.dialogMode);
    } else {
      await this.eventService.importEventsFromInspection(
        this.selectedInspection.value,
        this.selectedItem.value,
        this.inspectionId,
        this.currentInspectionSelectedItem.value,
      );
      this.dialogRef.close(true);
    }
  }

  public isValidForm(): boolean {
    return this.naturalForm?.isValid();
  }
}
